import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Youtube, Facebook, Twitter, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Welcome to the Village. Check your inbox.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="mt-24 border-t border-border bg-secondary/40 relative">
      <div className="container-x py-16 md:py-24 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-10">
        <div className="col-span-2 lg:col-span-2">
          <div className="font-display text-2xl leading-tight">Wolfdog Village</div>
          <p className="eyebrow mt-1">Learning Institute</p>
          <p className="mt-6 text-sm text-muted-foreground max-w-sm">
            Educating people. Strengthening relationships. Improving the future of wolfdogs — because every wolfdog deserves the right start.
          </p>
          <form onSubmit={subscribe} className="mt-6 flex items-stretch gap-0 max-w-sm" data-testid="footer-newsletter">
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              required
              placeholder="your@email.com"
              data-testid="footer-newsletter-email"
              className="flex-1 min-w-0 px-4 py-3 text-sm rounded-l-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="footer-newsletter-submit"
              className="rounded-r-md bg-primary text-primary-foreground px-4 hover:brightness-110 transition"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>
          <div className="flex items-center gap-4 mt-6 text-muted-foreground">
            <a href="#" aria-label="Instagram" className="hover:text-foreground"><Instagram className="h-4 w-4" /></a>
            <a href="#" aria-label="YouTube" className="hover:text-foreground"><Youtube className="h-4 w-4" /></a>
            <a href="#" aria-label="Facebook" className="hover:text-foreground"><Facebook className="h-4 w-4" /></a>
            <a href="#" aria-label="Twitter" className="hover:text-foreground"><Twitter className="h-4 w-4" /></a>
          </div>
        </div>

        <FooterCol title="Learn" links={[["All Courses","/learn"],["Learning Paths","/learn"],["Certification","/certification"],["Legal Center","/legal"]]} />
        <FooterCol title="Community" links={[["Discussions","/community"],["Member Spotlight","/community"],["Events","/events"],["News","/news"]]} />
        <FooterCol title="Resources" links={[["Library","/resources"],["Directory","/directory"],["Recommended Reading","/resources"],["Contact","/contact"]]} />
        <FooterCol title="About" links={[["Our Mission","/about"],["Our Team","/about"],["Partners","/about"],["Contact","/contact"]]} />
      </div>

      <div className="container-x pb-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-none opacity-90">
          Every Wolfdog<br /><span className="italic">deserves the right start.</span>
        </div>
      </div>

      <div className="border-t border-border">
        <div className="container-x py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <div>© {new Date().getFullYear()} Wolfdog Village Learning Institute. All rights reserved.</div>
          <div className="flex items-center gap-6">
            <Link to="/about" className="hover:text-foreground">Privacy Policy</Link>
            <Link to="/about" className="hover:text-foreground">Terms of Use</Link>
            <Link to="/about" className="hover:text-foreground">Donate</Link>
            <Link to="/about" className="hover:text-foreground">Partner With Us</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="eyebrow mb-4">{title}</div>
      <ul className="space-y-3 text-sm">
        {links.map(([label, to]) => (
          <li key={label}>
            <Link to={to} className="text-muted-foreground hover:text-foreground transition-colors">{label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
